/**
 * Telecom v2 — combined CSV Cybersecurity Domain Ratings & Commentary panel.
 *
 * Replaces the previously separate `CsvDomainScorecard` + the
 * "CSV Domain Commentary (Report Preview)" `DomainNarrativePanel`
 * for telecom_v2 assessments. Each of the seven CSV cybersecurity
 * domains is rendered as a single card row containing its name,
 * color-coded maturity label, and the narrative paragraph generated
 * from the same scoring data source (`calculate_csv_domain_scores` RPC).
 *
 * Reactive: refreshes whenever `refreshKey` bumps (e.g. after a reviewer
 * rating save), using the same data source the prior two sections used.
 * Does not expose numeric scores or percentages.
 */
import React, { useEffect, useState, useCallback, useMemo } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { BarChart3 } from "lucide-react";
import { csvDomainNarrative } from "@/reports/telecom/domainNarratives";
import {
  INFRASTRUCTURE_COVERAGE_NOTE_UI,
  INFRASTRUCTURE_PARTIAL_BADGE,
  type InfrastructureCoverage,
} from "@/reports/telecom/infrastructureCoverageNote";

const sb: any = supabase as any;

type DomainScore = {
  domain_name: string;
  average_score: number;
  maturity_level: string;
  controls_evaluated: number;
};

// Canonical display order for telecom_v2 CSV cybersecurity domains.
// Any domain returned by the scoring RPC that is not in this list is appended
// at the end in the order returned. Domains in this list that are NOT returned
// by the RPC (i.e. have no real questions/scores in the DB) are not rendered —
// we never display fake/empty domains.
const CSV_DOMAIN_ORDER: string[] = [
  "Risk Management",
  "Governance",
  "Supply Chain Management",
  "Asset Management",
  "Identity-Centric Security",
  "Security Operations",
  "Data Protection",
  "Infrastructure Security",
];

const MATURITY_STYLES: Record<string, { bg: string; text: string; border: string }> = {
  "Not Assessed": { bg: "#F3F4F6", text: "#4B5563", border: "#9CA3AF" },
  Minimal:   { bg: "#FEE2E2", text: "#7F1D1D", border: "#DC2626" },
  Adequate:  { bg: "#FEF3C7", text: "#78350F", border: "#F59E0B" },
  Strong:    { bg: "#DCFCE7", text: "#14532D", border: "#16A34A" },
  Extensive: { bg: "#DBEAFE", text: "#1E3A8A", border: "#2563EB" },
};

type Props = {
  assessmentId: string;
  /** Bump this to trigger a refresh */
  refreshKey?: number;
  /** Emits the average of evaluated CSV domain scores (0–2 scale) for cyber-resilience auto-calc. */
  onOverallScoreChange?: (score: number) => void;
};

export function CsvDomainRatingsAndCommentary({
  assessmentId,
  refreshKey,
  onOverallScoreChange,
}: Props) {
  const [dbScores, setDbScores] = useState<DomainScore[]>([]);
  const [coverage, setCoverage] = useState<InfrastructureCoverage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadScores = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [scoresRes, coverageRes] = await Promise.all([
        sb.rpc("calculate_csv_domain_scores", { p_assessment_id: assessmentId }),
        sb
          .rpc("reviewer_get_infrastructure_assessment_coverage", { p_assessment_id: assessmentId })
          .then((r: any) => r, (error: any) => ({ data: null, error })),
      ]);
      if (scoresRes.error) throw scoresRes.error;
      setDbScores((Array.isArray(scoresRes.data) ? scoresRes.data : []) as DomainScore[]);
      const cov = Array.isArray(coverageRes?.data) ? coverageRes.data[0] : coverageRes?.data;
      setCoverage((cov ?? null) as InfrastructureCoverage | null);
    } catch (e: any) {
      console.error("[CsvDomainRatingsAndCommentary] load error:", e);
      setError(e.message ?? "Failed to load CSV domain scores");
    } finally {
      setLoading(false);
    }
  }, [assessmentId]);

  useEffect(() => {
    loadScores();
  }, [loadScores, refreshKey]);

  // Render only domains actually returned by the scoring RPC (real data).
  // Sort by canonical order; any unknown domains appear at the end in DB order.
  const scores = useMemo(() => {
    const orderIdx = (name: string) => {
      const i = CSV_DOMAIN_ORDER.indexOf(name);
      return i === -1 ? Number.MAX_SAFE_INTEGER : i;
    };
    return [...dbScores].sort((a, b) => orderIdx(a.domain_name) - orderIdx(b.domain_name));
  }, [dbScores]);

  // Emit overall score (matches CsvDomainScorecard semantics).
  useEffect(() => {
    if (!onOverallScoreChange) return;
    const scored = scores.filter((s) => s.controls_evaluated > 0);
    if (scored.length === 0) {
      onOverallScoreChange(0);
      return;
    }
    const overall = scored.reduce((sum, s) => sum + Number(s.average_score), 0) / scored.length;
    onOverallScoreChange(overall);
  }, [scores, onOverallScoreChange]);

  if (loading) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-muted-foreground text-sm">
          Calculating CSV domain ratings…
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="py-8 text-center text-sm text-destructive">
          {error}
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2 text-base">
          <BarChart3 className="h-4 w-4 text-primary" />
          CSV Cybersecurity Domain Ratings &amp; Commentary
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-xs text-muted-foreground">
          Maturity rating and narrative interpretation for each CSV cybersecurity domain
          assessed for this engagement. Updates live as reviewer ratings change. Appears
          in the generated PDF report.
        </p>
        {scores.length === 0 && (
          <p className="text-xs text-muted-foreground italic">
            No CSV domain ratings yet — reviewer scoring is required before this section
            can be interpreted.
          </p>
        )}
        <div className="space-y-3">
          {scores.map((s) => {
            const isInfraNote =
              s.domain_name === "Infrastructure Security" &&
              s.maturity_level === "Not Assessed" &&
              coverage?.show_coverage_note === true;
            // Amber compact pill when infra coverage is incomplete; otherwise standard maturity styling.
            const partialStyle = { bg: "#FEF3C7", text: "#78350F", border: "#D97706" };
            const style = isInfraNote
              ? partialStyle
              : MATURITY_STYLES[s.maturity_level] ?? MATURITY_STYLES["Not Assessed"];
            const pillLabel = isInfraNote
              ? INFRASTRUCTURE_PARTIAL_BADGE
              : s.maturity_level;
            const text = isInfraNote
              ? INFRASTRUCTURE_COVERAGE_NOTE_UI
              : csvDomainNarrative(
                  s.domain_name,
                  Number(s.average_score),
                  Number(s.controls_evaluated ?? 0),
                );
            return (
              <div
                key={s.domain_name}
                className="rounded-lg border bg-card p-4 flex flex-col sm:flex-row gap-4"
              >
                <div className="sm:w-56 shrink-0 flex sm:flex-col items-center sm:items-start justify-between gap-2">
                  <span className="text-sm font-semibold text-foreground">
                    {s.domain_name}
                  </span>
                  <span
                    className="inline-block max-w-full rounded-full border px-3 py-1 text-center text-xs font-semibold leading-tight whitespace-normal"
                    style={{
                      backgroundColor: style.bg,
                      color: style.text,
                      borderColor: style.border,
                    }}
                  >
                    {pillLabel}
                  </span>
                </div>
                <p className="text-xs leading-relaxed text-muted-foreground flex-1">
                  {text}
                </p>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
